import { Component, ElementRef, HostListener, effect, signal, viewChild } from "@angular/core";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { ImageFlipperService, ImageFlipperServiceType, ShotOverlayPoint } from "../../copilot/image-flipper.service";
import { NzDrawerModule } from "ng-zorro-antd/drawer";
import { NzBadgeComponent } from "ng-zorro-antd/badge";
import { NzEmptyComponent } from "ng-zorro-antd/empty";
import { NzTooltipDirective } from "ng-zorro-antd/tooltip";
import { NzIconDirective } from "ng-zorro-antd/icon";
import { NzSelectModule } from "ng-zorro-antd/select";
import { FormsModule } from "@angular/forms";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzPopconfirmModule } from "ng-zorro-antd/popconfirm";
import { CommonModule } from "@angular/common";

@Component({
    imports: [NzDrawerModule, NzButtonModule, NzPopconfirmModule, NzBadgeComponent, NzEmptyComponent, NzTooltipDirective, NzIconDirective, NzSelectModule, FormsModule, CommonModule],
    selector: "app-xwai-image-flipper",
    templateUrl: "./xwai-image-flipper.component.html",
    styleUrls: ["./xwai-image-flipper.component.less"],
    standalone: true
})
export class XwaiImageFlipperComponent {
  private destroyed$ = new Subject<void>();
  drawer_visible: boolean = false;
  drawer_width: string = "fit-content";
  images: ImageFlipperServiceType[] = [];

  current_index = 0;
  // when true the stage image stretches to fill the stage (object-fit: fill)
  // instead of preserving its aspect ratio (object-fit: contain)
  fill_image = false;
  is_playing = false;
  flip_interval_ms = 1000;
  speed_options = [
    { label: "Slow (2s)", value: 2000 },
    { label: "Normal (1s)", value: 1000 },
    { label: "Fast (0.5s)", value: 500 },
    { label: "Very fast (0.25s)", value: 250 },
  ];
  private play_timer: any = null;

  // Hover state for the shot-overlay tooltip (position tracked relative to
  // the stage-image-wrap element, same pattern as XwaiSliceViewerComponent).
  protected hovered_shot = signal<ShotOverlayPoint | null>(null);
  protected tooltip_pos = signal<{ x: number; y: number }>({ x: 0, y: 0 });

  // Stage size and each image's natural size, used to place the shot overlay
  // exactly over the image's rendered box.
  private wrap_size = signal<{ width: number; height: number }>({ width: 0, height: 0 });
  private natural_sizes = signal<Record<string, { width: number; height: number }>>({});
  // The stage lives in lazily rendered drawer content, so it comes and goes.
  private image_wrap = viewChild<ElementRef<HTMLElement>>("imageWrap");

  constructor(
    private imageFlipper: ImageFlipperService,
    private notificationService: NzNotificationService
  ) {
    // Track the stage's size while it's rendered.
    effect((onCleanup) => {
      const wrap = this.image_wrap()?.nativeElement;
      if (!wrap) return;
      const observer = new ResizeObserver(([entry]) => {
        const { width, height } = entry.contentRect;
        this.wrap_size.set({ width, height });
      });
      observer.observe(wrap);
      onCleanup(() => observer.disconnect());
    });
  }

  ngOnInit() {
    this.imageFlipper.drawerVisible$.pipe(takeUntil(this.destroyed$)).subscribe((visible) => {
      this.drawer_visible = visible;
    });

    this.imageFlipper.images$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (images) => {
        this.images = images;
        // keep the current frame in range when images are removed
        this.current_index = Math.min(
          this.current_index,
          Math.max(0, this.images.length - 1)
        );
        if (this.images.length == 0) {
          this.drawer_width = "fit-content";
          this.stopPlayback();
        } else {
          this.drawer_width = "80%";
        }
      },
      error: (err) => {
        console.log(err);
        this.notificationService.error(
          "Error",
          err.error?.error ||
            "There was an error when getting all projects. Please try later"
        );
      },
    });
  }

  ngOnDestroy() {
    this.stopPlayback();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get currentImage(): ImageFlipperServiceType | null {
    return this.images[this.current_index] || null;
  }

  toggleDrawer() {
    this.imageFlipper.toggleDrawer();
  }

  closeDrawer() {
    this.imageFlipper.closeDrawer();
  }

  go_to_image(index: number) {
    this.current_index = index;
  }

  next() {
    if (this.images.length === 0) {
      return;
    }
    this.current_index = (this.current_index + 1) % this.images.length;
  }

  prev() {
    if (this.images.length === 0) {
      return;
    }
    this.current_index =
      (this.current_index - 1 + this.images.length) % this.images.length;
  }

  togglePlayback() {
    if (this.is_playing) {
      this.stopPlayback();
    } else {
      this.startPlayback();
    }
  }

  private startPlayback() {
    if (this.images.length < 2) {
      return;
    }
    this.is_playing = true;
    this.play_timer = setInterval(() => this.next(), this.flip_interval_ms);
  }

  private stopPlayback() {
    this.is_playing = false;
    if (this.play_timer) {
      clearInterval(this.play_timer);
      this.play_timer = null;
    }
  }

  onSpeedChange() {
    // restart the timer with the new interval
    if (this.is_playing) {
      this.stopPlayback();
      this.startPlayback();
    }
  }

  remove_image(index: number) {
    this.imageFlipper.remove_image_at_index(index);
  }

  clear_all() {
    this.imageFlipper.clear_images();
  }

  on_image_load(link: string, event: Event) {
    const img = event.target as HTMLImageElement;
    this.natural_sizes.update((sizes) => ({
      ...sizes,
      [link]: { width: img.naturalWidth, height: img.naturalHeight },
    }));
  }

  /**
   * The current image's rendered box within the stage: the whole stage when
   * stretched (object-fit: fill), otherwise the letterboxed contain box.
   */
  overlay_rect(): { left: number; top: number; width: number; height: number } {
    const { width, height } = this.wrap_size();
    const natural = this.currentImage && this.natural_sizes()[this.currentImage.image_link];
    if (this.fill_image || !natural?.width || !natural?.height) {
      return { left: 0, top: 0, width, height };
    }
    const scale = Math.min(width / natural.width, height / natural.height);
    const w = natural.width * scale;
    const h = natural.height * scale;
    return { left: (width - w) / 2, top: (height - h) / 2, width: w, height: h };
  }

  on_shot_enter(point: ShotOverlayPoint) {
    this.hovered_shot.set(point);
  }

  on_shot_move(event: MouseEvent, wrap: HTMLElement) {
    const rect = wrap.getBoundingClientRect();
    this.tooltip_pos.set({ x: event.clientX - rect.left, y: event.clientY - rect.top });
  }

  on_shot_leave() {
    this.hovered_shot.set(null);
  }

  @HostListener("document:keydown", ["$event"])
  handleKeydown(event: KeyboardEvent) {
    if (!this.drawer_visible || this.images.length === 0) {
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      this.next();
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.prev();
    }
  }
}
